import {
	addPdfInsightRingToBoard,
	closeAllPanels,
	componentToggle,
	createButtons,
	createDialog,
	showAlert,
} from './uiUtils';
import {
	getFilesOfBoard,
	getPdfAnalysis,
	PdfContextSourceView,
	PdfContextView,
	retryPdfAnalysis,
	updatePdfContext,
	uploadPdf,
} from '../apiRequests';
import { formatPdfDisplayName } from '../../../../src/pdfFileName';
import { isPdfBoardSeedAlreadyOnBoard } from '../../../../src/services/pdf/pdfBoardSeedSelection';
import Graph from '../graphs/Graph';

const pdfButton = document.querySelector<HTMLButtonElement>('#board-menu-upload');
let pollTimer: number | undefined;

function handlePdfButtonClick(event: Event) {
	event.preventDefault();
	const boardId = document.body.dataset.boardId!;
	closeAllPanels();
	pdfButton?.classList.add('buttonActive');
	componentToggle(document.getElementById('upload-container')!);
	void refreshPanel(boardId);
	document.querySelector<HTMLButtonElement>('#upload-button')!.onclick = uploadFiles;
}

const isProcessing = (status?: string): boolean =>
	['queued', 'extracting_text', 'extracting_graph'].includes(status || '');

const refreshPdfStatusBadge = (response: PdfContextView): void => {
	if (!pdfButton) return;
	let badge = pdfButton.querySelector<HTMLSpanElement>('.pdf-analysis-status');
	const localSources = response.sources.filter(({ currentBoard }) => currentBoard);
	if (!localSources.length && response.uploadStatus?.state !== 'processing') {
		badge?.remove();
		pdfButton.title = 'PDFs';
		return;
	}
	if (!badge) {
		badge = document.createElement('span');
		badge.className = 'pdf-analysis-status';
		pdfButton.append(badge);
	}
	const statuses = localSources.map(
		({ analysis }) => analysis?.ingestionStatus || analysis?.status || 'queued'
	);
	const processing = response.uploadStatus?.state === 'processing' || statuses.some(isProcessing);
	const failed =
		response.uploadStatus?.state === 'failed' || statuses.some((status) => status === 'failed');
	const status = processing ? 'processing' : failed ? 'failed' : 'ready';
	badge.textContent = processing ? '…' : failed ? '!' : '✓';
	badge.dataset.status = status;
	pdfButton.title = processing
		? 'PDFs · extracting document insights'
		: failed
			? 'PDFs · one or more documents need attention'
			: 'PDFs · document insights ready';
};

const refreshPanel = async (boardId: string): Promise<void> => {
	try {
		const response = await getFilesOfBoard(boardId);
		renderContextSources(response);
		refreshPdfStatusBadge(response);
		const processing =
			response.uploadStatus?.state === 'processing' ||
			response.sources.some(({ analysis }) =>
				isProcessing(analysis?.ingestionStatus || analysis?.status)
			);
		window.clearTimeout(pollTimer);
		if (processing) {
			pollTimer = window.setTimeout(() => void refreshPanel(boardId), 2000);
		}
	} catch (error) {
		console.error('Unable to load PDF context:', error);
	}
};

const uploadFiles = async (event: Event): Promise<void> => {
	event.preventDefault();
	const boardId = document.body.dataset.boardId!;
	const fileInput = document.querySelector<HTMLInputElement>('#fileInput')!;
	const files = [...(fileInput.files || [])];
	if (!files.length) return;
	try {
		if (await uploadPdf(boardId, files)) {
			fileInput.value = '';
			await refreshPanel(boardId);
			return;
		}
		showAlert('The PDFs could not be uploaded. Please try again.');
	} catch (error) {
		console.error('Unable to upload PDFs:', error);
		showAlert('The PDFs could not be uploaded. Please try again.');
	}
};

const refKey = (boardId: string, fileId: string): string => `${boardId}:${fileId}`;

const addPdfNode = (
	graph: Graph,
	label: string,
	metadata: {
		pdfInsightId?: string;
		pdfAnalysisId?: string;
		pdfFileName?: string;
		pdfProvenance?: 'legacy_derived' | 'source_extracted' | 'source_inferred';
	}
): boolean => {
	if (
		metadata.pdfInsightId &&
		graph.nodes.some((node) => node.pdfInsightId === metadata.pdfInsightId)
	) {
		return false;
	}
	graph.addNodeNearPoint({ x: -380, y: 50 }, label, undefined, undefined, false, false, metadata);
	graph.zoomToFit();
	return true;
};

const showDocumentInsightsPanel = (source: PdfContextSourceView): void => {
	const displayName = formatPdfDisplayName(source.file.fileName);
	const content = document.createElement('div');
	content.className = 'pdf-insights-content';
	content.innerHTML = '<p><em>Loading document analysis…</em></p>';
	createDialog('PDF insights', [content], createButtons([{ label: 'Close', primary: true }], true));

	const render = async (): Promise<void> => {
		try {
			const analysis = await getPdfAnalysis(source.boardId, source.file.fileId);
			if (!content.isConnected) return;
			const attemptStatus = analysis.ingestionStatus || analysis.status;
			content.replaceChildren();
			const documentName = document.createElement('h4');
			documentName.className = 'pdf-insights-document-name';
			documentName.textContent = displayName;
			content.append(documentName);

			const status = document.createElement('p');
			status.className = 'pdf-insights-status';
			const publishedStatus = (analysis.publishedStatus || analysis.status).replace(/_/g, ' ');
			status.textContent = `${
				publishedStatus === 'partial' ? 'Usable with limited coverage' : `Published: ${publishedStatus}`
			}${
				attemptStatus !== analysis.status
					? ` · Latest extraction: ${attemptStatus.replace(/_/g, ' ')}`
					: ''
			}`;
			content.append(status);

			if (analysis.pageCount) {
				const pages = document.createElement('p');
				pages.className = 'pdf-insights-pages';
				pages.textContent = `${analysis.extractedPageCount || 0} of ${analysis.pageCount} pages extracted`;
				content.append(pages);
			}

			if (analysis.reuploadRequired && analysis.status !== 'failed') {
				const traceability = document.createElement('div');
				traceability.className = 'pdf-insights-notice';
				const message = document.createElement('p');
				message.textContent =
					'This legacy analysis is usable, but page-level evidence requires the source PDF.';
				const reupload = document.createElement('button');
				reupload.type = 'button';
				reupload.textContent = 'Re-upload for page evidence';
				reupload.onclick = () => document.querySelector<HTMLInputElement>('#fileInput')?.click();
				traceability.append(message, reupload);
				content.append(traceability);
			}

			if (analysis.overview) {
				const summary = document.createElement('section');
				summary.className = 'pdf-summary';
				const heading = document.createElement('h4');
				heading.textContent = 'Summary';
				const overview = document.createElement('p');
				overview.textContent = analysis.overview;
				const add = document.createElement('button');
				add.type = 'button';
				const syncSeedActionState = (): void => {
					const graph = globalThis.neuroCreate.graph;
					const alreadyOnBoard = Boolean(
						graph &&
						isPdfBoardSeedAlreadyOnBoard({
							nodes: graph.nodes,
							analysisId: analysis.analysisId,
							insights: analysis.insights || [],
						})
					);
					add.disabled = alreadyOnBoard;
					add.textContent = alreadyOnBoard
						? 'Summary + insights already on board'
						: 'Add summary + insights to board';
					add.title = alreadyOnBoard
						? 'The summary and first layer of extracted insights are already on this board'
						: 'Adds the summary and the first layer of extracted insights';
				};
				add.onclick = () => {
					const graph = globalThis.neuroCreate.graph;
					if (!graph) return;
					addPdfInsightRingToBoard(graph, analysis);
					syncSeedActionState();
				};
				syncSeedActionState();
				summary.append(heading, overview, add);
				content.append(summary);
			}

			if (analysis.keywords?.length) {
				const keywords = document.createElement('p');
				keywords.className = 'pdf-insights-keywords';
				const keywordLabel = document.createElement('strong');
				keywordLabel.textContent = 'Keywords: ';
				keywords.append(
					keywordLabel,
					analysis.keywords
						.slice(0, 12)
						.map(({ text }) => text)
						.join(', ')
				);
				content.append(keywords);
			}

			const insights = (analysis.insights || [])
				.filter((insight) => insight.label !== 'Document overview')
				.sort(
					(left, right) => (left.level || 4) - (right.level || 4) || right.salience - left.salience
				);
			if (insights.length) {
				const heading = document.createElement('h4');
				heading.textContent = 'Extracted insights';
				content.append(heading);
				for (const insight of insights) {
					const row = document.createElement('div');
					row.className = 'pdf-insight-row';
					const evidencePages = [
						...new Set(insight.evidence.map(({ page }) => page).filter(Boolean)),
					];
					const text = document.createElement('div');
					const title = document.createElement('strong');
					title.textContent = `L${insight.level || '–'} · ${insight.label}`;
					const metadata = document.createElement('small');
					metadata.textContent = `${insight.kind}${
						evidencePages.length ? ` · page ${evidencePages.join(', ')}` : ''
					}`;
					const description = document.createElement('p');
					description.textContent = insight.description;
					text.append(title, metadata, description);
					const add = document.createElement('button');
					add.type = 'button';
					add.textContent = 'Add';
					add.onclick = () => {
						const graph = globalThis.neuroCreate.graph;
						if (
							graph &&
							addPdfNode(graph, insight.label, {
								pdfInsightId: insight.insightId,
								pdfAnalysisId: analysis.analysisId,
								pdfFileName: analysis.fileName,
								pdfProvenance: insight.provenance,
							})
						) {
							add.textContent = 'Added';
							add.disabled = true;
						} else if (graph) {
							add.textContent = 'On board';
							add.disabled = true;
						}
					};
					row.append(text, add);
					content.append(row);
				}
			}

			if (attemptStatus === 'failed') {
				const failure = document.createElement('div');
				failure.className = 'pdf-insights-notice is-error';
				const error = document.createElement('p');
				error.textContent = analysis.publishedStatus
					? `The published insights remain usable. The latest extraction failed: ${analysis.error?.message || 'unknown error'}`
					: analysis.error?.message || 'Document analysis failed.';
				const retry = document.createElement('button');
				retry.type = 'button';
				retry.textContent = analysis.reuploadRequired ? 'Re-upload PDF' : 'Retry analysis';
				retry.onclick = async () => {
					if (analysis.reuploadRequired) {
						document.querySelector<HTMLInputElement>('#fileInput')?.click();
						return;
					}
					retry.disabled = true;
					retry.textContent = 'Retrying…';
					await retryPdfAnalysis(source.boardId, source.file.fileId);
					await render();
				};
				failure.append(error, retry);
				content.append(failure);
			}

			if (isProcessing(attemptStatus)) {
				const progress = document.createElement('p');
				progress.className = 'pdf-insights-progress';
				progress.textContent =
					'Extraction is still running. Summary and insights will appear here automatically.';
				content.append(progress);
				window.setTimeout(() => void render(), 1500);
			}
		} catch (error) {
			console.error('Unable to load PDF insights:', error);
			if (content.isConnected) {
				content.innerHTML =
					'<p>Document insights are unavailable. Select or re-upload the PDF to continue.</p>';
			}
		}
	};

	void render();
};

const renderContextSources = (response: PdfContextView): void => {
	const container = document.getElementById('file-list');
	if (!container) return;
	container.replaceChildren();
	const boardId = document.body.dataset.boardId!;
	const selected = new Set(response.context.map((ref) => refKey(ref.boardId, ref.fileId)));
	let updateInFlight = false;
	const setContextInputsDisabled = (disabled: boolean): void => {
		for (const input of container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
			input.disabled = disabled;
		}
	};
	const sections = [
		{ title: 'This board', sources: response.sources.filter((source) => source.currentBoard) },
		{
			title: 'Other boards in this project',
			sources: response.sources.filter((source) => !source.currentBoard),
		},
	];
	for (const section of sections) {
		if (!section.sources.length) continue;
		const heading = document.createElement('li');
		heading.className = 'pdf-context-section';
		heading.textContent = section.title;
		container.append(heading);
		for (const source of section.sources) {
			const displayName = formatPdfDisplayName(source.file.fileName);
			const item = document.createElement('li');
			item.className = 'file-item';
			const checkbox = document.createElement('input');
			checkbox.type = 'checkbox';
			checkbox.checked = selected.has(refKey(source.boardId, source.file.fileId));
			checkbox.setAttribute('aria-label', `Use ${displayName} as context`);
			const info = document.createElement('div');
			info.className = 'file-info';
			const name = document.createElement('strong');
			name.className = 'file-name';
			name.textContent = displayName;
			name.title = displayName;
			const meta = document.createElement('small');
			const analysisStatus =
				source.analysis?.ingestionStatus || source.analysis?.status || 'preparing';
			meta.textContent = `${source.boardTitle} · ${analysisStatus.replace(/_/g, ' ')}`;
			info.append(name, meta);
			if (source.analysis?.overview) {
				const summary = document.createElement('small');
				summary.className = 'file-summary';
				summary.textContent = source.analysis.overview;
				info.append(summary);
			}
			const actions = document.createElement('div');
			actions.className = 'file-actions';
			const insights = document.createElement('button');
			insights.type = 'button';
			insights.className = 'view-pdf-insights';
			insights.textContent = 'View insights';
			insights.onclick = () => showDocumentInsightsPanel(source);
			actions.append(insights);
			item.append(checkbox, info, actions);
			checkbox.onchange = async () => {
				if (updateInFlight) return;
				updateInFlight = true;
				setContextInputsDisabled(true);
				const key = refKey(source.boardId, source.file.fileId);
				if (checkbox.checked) selected.add(key);
				else selected.delete(key);
				const context = response.sources
					.filter((candidate) => selected.has(refKey(candidate.boardId, candidate.file.fileId)))
					.map((candidate) => ({
						boardId: candidate.boardId,
						fileId: candidate.file.fileId,
					}));
				try {
					await updatePdfContext(boardId, context);
				} catch (error) {
					console.error('Unable to update PDF context:', error);
					if (checkbox.checked) selected.delete(key);
					else selected.add(key);
					checkbox.checked = !checkbox.checked;
					showAlert('The PDF context could not be updated.');
				} finally {
					updateInFlight = false;
					setContextInputsDisabled(false);
				}
			};
			container.append(item);
		}
	}
	if (!response.sources.length) {
		const empty = document.createElement('li');
		empty.className = 'pdf-context-empty';
		empty.textContent =
			response.uploadStatus?.state === 'processing'
				? 'Uploading and preparing PDFs…'
				: 'No PDFs have been uploaded yet.';
		container.append(empty);
	}
};

if (pdfButton) {
	pdfButton.onclick = handlePdfButtonClick;
	void refreshPanel(document.body.dataset.boardId!);
}
